import { Component, inject, TemplateRef, viewChild, AfterViewInit, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';

import {
  DataDisplayComponent,
  DataDisplayItem,
  ButtonComponent,
  BadgeComponent
} from 'htx-ui-library';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { map, Observable, switchMap, catchError, of, combineLatest, BehaviorSubject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  OrganizationMemberDetails,
  ActivityState
} from '../../../../../services/organizations/organization.model';
import { OrganizationService } from '../../../../../services/organizations/organization.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import { NavigationService } from '../../../../../services/navigation.service';
import { ProtectedContentComponent } from '../../../../organisms/protected-content/protected-content.component';

@Component({
  selector: 'app-organization-member-detail-page',
  imports: [
    AsyncPipe,
    DataDisplayComponent,
    TranslocoDirective,
    TranslocoPipe,
    ButtonComponent,
    BadgeComponent,
    ProtectedContentComponent
  ],
  templateUrl: './organization-member-detail-page.component.html',
  styleUrl: './organization-member-detail-page.component.scss'
})
export class OrganizationMemberDetailPageComponent implements AfterViewInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private translocoService = inject(TranslocoService);
  private organizationService = inject(OrganizationService);
  private notificationService = inject(NotificationService);
  private navigationService = inject(NavigationService);

  requiredRoles = this.navigationService.getAccessRoles('admin/organizations');

  isOrganizationPending = signal(false);

  constructor() {
    this.organizationService.isOrganizationPending$().subscribe(pending => {
      this.isOrganizationPending.set(pending);
    });
  }

  rolesTemplateRef = viewChild<TemplateRef<any>>('rolesTemplate');
  private rolesTemplate$ = new BehaviorSubject<TemplateRef<any> | undefined>(undefined);

  organizationId$ = this.activatedRoute.paramMap.pipe(map((data) => data.get('organizationId')!));
  memberId$ = this.activatedRoute.paramMap.pipe(map((data) => data.get('memberId')!));

  data$: Observable<OrganizationMemberDetails> = this.activatedRoute.paramMap.pipe(
    switchMap((params) => {
      const memberId = params.get('memberId')!;
      return this.organizationService.findMemberDetails(memberId);
    }),
    catchError(() => {
      this.notificationService.showError('organization.member.detail.loadingError', 5000);
      return of();
    })
  );

  memberDetails$: Observable<DataDisplayItem[]> = combineLatest([
    this.data$,
    this.rolesTemplate$
  ]).pipe(
    map(([member, rolesTmpl]) => {
      const statusKey =
        member.activityState === ActivityState.ACTIVE
          ? 'organization.member.status.active'
          : 'organization.member.status.inactive';

      const items: DataDisplayItem[] = [
        ...(member.title
          ? [
              {
                label: this.translocoService.translate('organization.member.title.label'),
                value: [this.translocoService.translate(`registration.page.userForm.controls.salutation.options.${member.title}`)]
              }
            ]
          : []),
        {
          label: this.translocoService.translate('organization.member.firstName.label'),
          value: [member.firstName]
        },
        {
          label: this.translocoService.translate('organization.member.lastName.label'),
          value: [member.lastName]
        },
        {
          label: this.translocoService.translate('organization.member.email.label'),
          value: [member.email]
        },
        {
          label: this.translocoService.translate('organization.member.phoneNumber.label'),
          value: [member.phoneNumber]
        },
        {
          label: this.translocoService.translate('organization.member.position.label'),
          value: [member.position]
        },
        {
          label: this.translocoService.translate('organization.member.roles.label'),
          ...(rolesTmpl ? { template: rolesTmpl } : { value: [member.roles.join(', ')] })
        },
        {
          label: this.translocoService.translate('organization.member.status.label'),
          value: [this.translocoService.translate(statusKey)]
        }
      ];
      return items;
    })
  );

  memberRoles$ = this.data$.pipe(
    map((member: OrganizationMemberDetails) => member.roles.map((r) => r.toLowerCase()))
  );

  ngAfterViewInit(): void {
    this.rolesTemplate$.next(this.rolesTemplateRef());
  }

  backToList() {
    this.organizationId$.subscribe((id) => {
      void this.router.navigate(['admin', 'organizations', 'details', id]);
    });
  }

  edit() {
    this.activatedRoute.paramMap.subscribe((params) => {
      const organizationId = params.get('organizationId')!;
      const memberId = params.get('memberId')!;
      void this.router.navigate([
        'admin',
        'organizations',
        'details',
        organizationId,
        'members',
        memberId,
        'edit'
      ]);
    });
  }
}
